import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';
import { OnboardingService } from '../../../services/onboarding.service';
import { AuthService } from '../../../services/auth.service';
import { TeamCatalogService } from '../../../services/team-catalog.service';
import { ONBOARDING_DOELEN } from '../../../interfaces/onboarding.interface';
import { welkomStappen, WelkomStap } from '../../../helpers/onboarding';
import { opAlfabet } from '../../../helpers/alfabetisch';

/** De profielen waarvoor er een naam en uitleg in i18n staat. */
const BEKENDE_PROFIELEN = ['coach', 'analyst', 'club_admin', 'player', 'manager', 'scout'];

/**
 * Welkomstvenster (variant A, docs/ONBOARDING.md): midden op het scherm,
 * één vraag per stap -- profiel, wat je wilt doen, team -- met het platform
 * gedimd erachter. Stap 3 valt weg bij één team; dat team gaat dan vanzelf
 * mee. "Skip" bewaart `overgeslagen`, de laatste stap bewaart de antwoorden
 * en sluit; de kaart "Getting started" op de startpagina neemt het over.
 *
 * Opent vanzelf als GET /Onboarding/Me `tonen` zegt, of via Help ->
 * "Getting started" voor wie de vragen nooit beantwoordde.
 */
@Component({
  selector: 'app-welkom-dialoog',
  standalone: true,
  imports: [TranslateModule],
  templateUrl: './welkom-dialoog.component.html',
  styleUrls: ['./welkom-dialoog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WelkomDialoogComponent {
  public readonly onboarding = inject(OnboardingService);
  private readonly auth = inject(AuthService);
  private readonly teamCatalog = inject(TeamCatalogService);
  private readonly router = inject(Router);

  /** De ploegen van de gebruiker; uit het token, zoals de ploegenkeuze. */
  private readonly teams: string[] = this.auth.user?.team ?? [];

  public readonly stappen: WelkomStap[] = welkomStappen(this.teams);
  public readonly index = signal(0);
  public readonly stap = computed(() => this.stappen[this.index()]);
  public readonly laatste = computed(() => this.index() === this.stappen.length - 1);

  public readonly profiel = signal<string | null>(null);
  public readonly doelen = signal<string[]>([]);
  public readonly team = signal<string | null>(null);
  public readonly bezig = signal(false);

  /** De profielen uit het antwoord van de server, in hun vaste volgorde. */
  public readonly profielen = signal<string[]>([]);
  /** Vaste volgorde uit het contract; geen alfabet. */
  public readonly alleDoelen = ONBOARDING_DOELEN;
  /** Ploegen op alfabet (keuzelijst), met hun naam uit de catalogus. */
  public readonly ploegen = signal<{ sleutel: string; naam: string }[]>([]);

  public readonly magVerder = computed(() => {
    switch (this.stap()) {
      case 'profiel': return !!this.profiel();
      case 'doelen': return this.doelen().length > 0;
      case 'team': return !!this.team();
      default: return false;
    }
  });

  constructor() {
    // Bij elke keer openen vers beginnen, met wat er al bekend is.
    effect(() => {
      if (this.onboarding.welkomOpen()) {
        untracked(() => this.begin());
      }
    });
  }

  public kiesProfiel(sleutel: string): void {
    this.profiel.set(sleutel);
  }

  public wisselDoel(sleutel: string): void {
    const huidig = this.doelen();
    // In de vaste volgorde bewaren, niet in de volgorde van aanklikken.
    const nieuw = huidig.includes(sleutel) ? huidig.filter(d => d !== sleutel) : [...huidig, sleutel];
    this.doelen.set(this.alleDoelen.filter(d => nieuw.includes(d)));
  }

  public kiesTeam(sleutel: string): void {
    this.team.set(sleutel);
  }

  public onTerug(): void {
    this.index.update(i => Math.max(0, i - 1));
  }

  public onVolgende(): void {
    if (!this.magVerder()) {
      return;
    }
    if (!this.laatste()) {
      this.index.update(i => i + 1);
      return;
    }
    this.bewaar(false);
  }

  public onOverslaan(): void {
    this.bewaar(true);
  }

  private bewaar(overgeslagen: boolean): void {
    if (this.bezig()) {
      return;
    }
    this.bezig.set(true);
    const team = this.stappen.includes('team') ? this.team() : (this.teams[0] ?? null);
    this.onboarding.bewaar({
      profiel: overgeslagen ? null : this.profiel(),
      doelen: overgeslagen ? [] : this.doelen(),
      team: overgeslagen ? null : team,
      overgeslagen,
    }).pipe(finalize(() => this.bezig.set(false))).subscribe({
      next: () => {
        this.onboarding.welkomOpen.set(false);
        // Naar de startpagina, waar de kaart "Getting started" nu staat.
        if (!overgeslagen) {
          void this.router.navigateByUrl('/');
        }
      },
      // Overslaan mag nooit blijven hangen; de vragen komen dan bij de
      // volgende keer inloggen terug. Bij bewaren blijft het venster open
      // (de foutmelding komt van de interceptor).
      error: () => {
        if (overgeslagen) {
          this.onboarding.welkomOpen.set(false);
        }
      },
    });
  }

  private begin(): void {
    const staat = this.onboarding.staat;
    this.index.set(0);
    this.profiel.set(staat?.profiel ?? null);
    this.doelen.set(staat?.doelen ?? []);
    this.team.set(staat?.team ?? null);
    this.profielen.set((staat?.profielen ?? []).map(p => p.sleutel).filter(s => BEKENDE_PROFIELEN.includes(s)));
    this.zetPloegen();
    void this.teamCatalog.ensureLoaded().then(() => this.zetPloegen());
  }

  private zetPloegen(): void {
    const ploegen = this.teams.map(sleutel => ({ sleutel, naam: this.teamCatalog.caption(sleutel) }));
    this.ploegen.set(opAlfabet(ploegen, p => p.naam));
  }
}
